import { useState } from "react";
import { changeTenantSuperAdminPassword } from "../../../../api/adminConsoleAuth";
import { getUserFacingMessage } from "../../../../api/errors";
import { loginSubAdmin, patchSubAdmin } from "../../../../api/subAdmins";
import { useAdminData } from "../../AdminDataContext";
import { adminAuth } from "../../utils/adminAuth";

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
  bg: string;
}

function getPasswordStrength(pwd: string): PasswordStrength {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;

  if (score <= 1) return { score, label: "弱", color: "text-red-500", bg: "bg-red-500" };
  if (score <= 3) return { score, label: "中", color: "text-amber-500", bg: "bg-amber-500" };
  return { score, label: "强", color: "text-green-600", bg: "bg-green-500" };
}

export default function PasswordSecurity() {
  const { tenantLegacyId } = useAdminData();
  const user = adminAuth.getLoggedInUser();
  const isSuper = user?.role === "super";
  const subCanChange =
    !isSuper && !!user?.id && !!user?.account?.trim();

  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const strength = newPwd ? getPasswordStrength(newPwd) : null;

  const validate = (): string => {
    if (!isSuper && !subCanChange) {
      return "无法修改密码，请退出后使用子账号重新登录再试。";
    }
    if (!oldPwd) return "请输入当前密码";
    if (!newPwd) return "请输入新密码";
    if (newPwd.length < 6) return "新密码至少 6 个字符";
    if (newPwd === oldPwd) return "新密码不能与当前密码相同";
    if (newPwd !== confirmPwd) return "两次输入的新密码不一致";
    return "";
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    setError("");
    if (isSuper) {
      void changeTenantSuperAdminPassword(tenantLegacyId, oldPwd, newPwd)
        .then(() => {
          setSuccess(true);
          setOldPwd("");
          setNewPwd("");
          setConfirmPwd("");
          window.setTimeout(() => setSuccess(false), 3000);
        })
        .catch((submitErr) =>
          setError(
            getUserFacingMessage(submitErr, "保存失败：当前密码可能不正确，或服务暂不可用，请稍后再试。"),
          ),
        )
        .finally(() => setSaving(false));
      return;
    }

    const ac = user!.account!.trim();
    const subId = user!.id!;
    void (async () => {
      try {
        await loginSubAdmin(tenantLegacyId, ac, oldPwd);
      } catch (verifyErr) {
        setError(
          getUserFacingMessage(verifyErr, "当前密码不正确，或服务暂不可用，请稍后再试。"),
        );
        setSaving(false);
        return;
      }
      try {
        await patchSubAdmin(tenantLegacyId, subId, { password: newPwd.trim() });
        setSuccess(true);
        setOldPwd("");
        setNewPwd("");
        setConfirmPwd("");
        window.setTimeout(() => setSuccess(false), 3000);
      } catch (patchErr) {
        setError(getUserFacingMessage(patchErr, "新密码保存失败，请稍后再试。"));
      } finally {
        setSaving(false);
      }
    })();
  };

  if (!isSuper && !subCanChange) {
    return (
      <div className="max-w-xl bg-white rounded-2xl p-6 text-sm text-gray-600 leading-relaxed">
        <p className="font-medium text-gray-800 mb-2">当前会话无法在本页修改密码</p>
        <p>
          子账号需携带登录账号信息才能修改自己的密码。请退出登录后，使用子账号账号密码重新登录，再进入「系统设置 →
          密码安全」操作。超级管理员仍可在「用户权限」中重置子账号密码。
        </p>
      </div>
    );
  }

  const cardTitle = isSuper ? "修改超级管理员密码" : "修改子账号登录密码";
  const cardSubtitle = isSuper
    ? "修改后将立即生效，下次登录需使用新密码"
    : `当前账号：${user?.account ?? ""}，修改后立即生效`;

  return (
    <div className="max-w-xl">
      <div className="bg-white rounded-2xl p-6 mb-4">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-lock-password-line text-red-600 text-lg"></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-800">{cardTitle}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{cardSubtitle}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current password */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              当前密码 <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <i className="ri-key-2-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type={showOld ? "text" : "password"}
                value={oldPwd}
                onChange={(e) => { setOldPwd(e.target.value); setError(""); }}
                placeholder="请输入当前密码"
                className="w-full pl-9 pr-11 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-400"
              />
              <button
                type="button"
                onClick={() => setShowOld(!showOld)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600"
              >
                <i className={showOld ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"}></i>
              </button>
            </div>
          </div>

          {/* New password */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              新密码 <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type={showNew ? "text" : "password"}
                value={newPwd}
                onChange={(e) => { setNewPwd(e.target.value); setError(""); }}
                placeholder="至少 6 个字符"
                className="w-full pl-9 pr-11 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-400"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600"
              >
                <i className={showNew ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"}></i>
              </button>
            </div>
            {/* Strength indicator */}
            {strength && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-400">密码强度</span>
                  <span className={`text-xs font-semibold ${strength.color}`}>{strength.label}</span>
                </div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-all ${i <= strength.score ? strength.bg : "bg-gray-100"}`}
                    ></div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1.5">建议使用大写字母、数字和特殊字符（如 @#!）</p>
              </div>
            )}
          </div>

          {/* Confirm */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              确认新密码 <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type={showConfirm ? "text" : "password"}
                value={confirmPwd}
                onChange={(e) => { setConfirmPwd(e.target.value); setError(""); }}
                placeholder="再次输入新密码"
                className={`w-full pl-9 pr-11 py-2.5 border rounded-xl text-sm focus:outline-none transition-colors ${
                  confirmPwd && newPwd && confirmPwd !== newPwd
                    ? "border-red-300 focus:border-red-500"
                    : confirmPwd && confirmPwd === newPwd
                    ? "border-green-400 focus:border-green-500"
                    : "border-gray-200 focus:border-red-400"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600"
              >
                <i className={showConfirm ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"}></i>
              </button>
              {confirmPwd && newPwd && confirmPwd === newPwd && (
                <i className="ri-check-line absolute right-9 top-1/2 -translate-y-1/2 text-green-500 text-sm"></i>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 rounded-xl text-red-600 text-xs">
              <i className="ri-error-warning-line flex-shrink-0"></i>
              {error}
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-green-50 rounded-xl text-green-700 text-xs">
              <i className="ri-checkbox-circle-line flex-shrink-0"></i>
              密码修改成功！下次登录请使用新密码
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={saving || !oldPwd || !newPwd || !confirmPwd}
              className="flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i>保存中...</>
              ) : (
                <><i className="ri-save-line"></i>保存修改</>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Tips */}
      <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
        <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
          <i className="ri-shield-check-line"></i>密码安全建议
        </p>
        <ul className="space-y-1.5">
          {[
            "密码长度建议 12 位以上",
            "混合使用大小写字母、数字与特殊字符",
            "避免使用生日、手机号等容易猜测的信息",
            "定期更换密码（建议每 3 个月一次）",
            "不要在多个平台使用同一密码",
          ].map((tip, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-amber-700">
              <i className="ri-check-line text-amber-500 flex-shrink-0 mt-0.5"></i>
              {tip}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
